import { useEffect, useState } from 'react';
import { CheckCircle, Clock, XCircle, AlertCircle, Mail } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';

export default function PaymentSuccess() {
  const { search } = useLocation();
  const ref = new URLSearchParams(search).get('ref');
  const [status, setStatus] = useState('checking');
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!ref) return;

    let ignore = false;
    let timer = null;
    const terminal = new Set(['paid', 'failed', 'cancelled']);

    async function fetchStatus() {
      try {
        const { data: responseData } = await axios.get(`${API_URL}/api/subscriptions/status`, {
          params: { ref: encodeURIComponent(ref) },
        });

        const next = (responseData?.status || responseData?.payment_status || 'unknown').toLowerCase();
        if (!ignore) {
          setStatus(next);
          setData(responseData);
          if (terminal.has(next) && timer) {
            clearInterval(timer);
            timer = null;
          }
        }
      } catch {
        if (!ignore) setStatus('unknown');
      }
    }

    fetchStatus();
    timer = setInterval(fetchStatus, 2500);

    return () => {
      ignore = true;
      if (timer) clearInterval(timer);
    };
  }, [ref]);

  const getStatusConfig = () => {
    switch (status) {
      case 'paid':
        return {
          icon: CheckCircle,
          color: 'text-green-500',
          bgColor: 'bg-green-50',
          title: 'Payment Successful! 🎉',
          message: 'Your subscription has been activated successfully.',
        };
      case 'failed':
        return {
          icon: XCircle,
          color: 'text-red-500',
          bgColor: 'bg-red-50',
          title: 'Payment Failed',
          message: "We couldn't process your payment. Please try again.",
        };
      case 'cancelled':
        return {
          icon: AlertCircle,
          color: 'text-amber-500',
          bgColor: 'bg-amber-50',
          title: 'Payment Cancelled',
          message: 'Your payment was cancelled.',
        };
      case 'checking':
        return {
          icon: Clock,
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          title: 'Verifying Payment...',
          message: 'Please wait while we confirm your payment.',
        };
      default:
        return {
          icon: AlertCircle,
          color: 'text-gray-500',
          bgColor: 'bg-secondary/40',
          title: 'Status Unknown',
          message: "We're checking your payment status.",
        };
    }
  };

  const statusConfig = getStatusConfig();
  const StatusIcon = statusConfig.icon;

  const formatAmount = (amountMinor, currency) => {
    const amount = amountMinor / 100;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency || 'INR',
    }).format(amount);
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className="min-h-screen bg-background px-4 py-12 overflow-x-hidden">
      <div className="mx-auto flex min-h-[calc(100vh-6rem)] max-w-md items-center justify-center">
        <div className="card w-full overflow-hidden">
          <div className={`${statusConfig.bgColor} relative px-8 py-12 text-center`}>
            <div className="absolute inset-0 opacity-70">
              <div className="absolute -left-10 -top-10 h-32 w-32 rounded-full bg-white/70 blur-2xl" />
              <div className="absolute -bottom-10 -right-10 h-32 w-32 rounded-full bg-white/60 blur-2xl" />
            </div>
            <div className="relative">
              <div className="mb-4 inline-flex items-center justify-center rounded-full bg-white/80 p-4 shadow-soft">
                <StatusIcon className={`h-12 w-12 ${statusConfig.color} ${status === 'checking' ? 'animate-pulse' : ''}`} />
              </div>
              <h1 className="section-title mb-2 text-2xl">{statusConfig.title}</h1>
              <p className="text-secondary-text">{statusConfig.message}</p>
            </div>
          </div>

          <div className="px-8 py-6">
            {status === 'paid' && (
              <div className="alert-info mb-6 flex items-start gap-3">
                <Mail className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
                <div className="flex-1">
                  <p className="mb-1 text-sm font-medium text-blue-600">Email Confirmation Sent</p>
                  <p className="text-xs text-blue-600">A payment confirmation has been sent to your registered email address.</p>
                </div>
              </div>
            )}

            {data && status === 'paid' && (
              <div className="mb-6 space-y-4">
                <div className="flex items-center justify-between border-b border-border py-3">
                  <span className="text-sm text-secondary-text">Amount</span>
                  <span className="font-semibold text-text">{formatAmount(data.amount_minor, data.currency)}</span>
                </div>
                <div className="flex items-center justify-between border-b border-border py-3">
                  <span className="text-sm text-secondary-text">Plan</span>
                  <span className="font-semibold text-text">Tier {data.tier}</span>
                </div>
                <div className="flex items-center justify-between border-b border-border py-3">
                  <span className="text-sm text-secondary-text">Date</span>
                  <span className="text-sm font-medium text-text">{formatDate(data.created_at)}</span>
                </div>
              </div>
            )}

            <div className="subtle-panel mb-6 p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-secondary-text">Reference ID</span>
                <code className="rounded-xl border border-border bg-white px-3 py-1 text-xs font-mono text-text">
                  {ref || 'n/a'}
                </code>
              </div>
            </div>

            {status === 'paid' && (
              <a href="/" className="primary-btn flex w-full justify-center py-3 text-center">
                Go to Dashboard
              </a>
            )}

            {status === 'failed' && (
              <a href="/enquiry?via=get-started" className="secondary-btn flex w-full justify-center py-3 text-center">
                Try Again
              </a>
            )}

            {status === 'checking' && (
              <div className="flex items-center justify-center text-sm text-secondary-text">
                <Clock className="mr-2 h-4 w-4 animate-spin" />
                Checking payment status...
              </div>
            )}
          </div>
        </div>
      </div>

      <p className="mt-6 text-center text-sm text-secondary-text">Need help? Contact our support team</p>
    </div>
  );
}
